import React, { useMemo } from 'react';

import classnames from 'classnames';
import uniqBy from 'lodash/uniqBy';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import EntityName from 'components/Globals/EntityName';
import { DurationAndLangInfo } from 'components/Productions/Display/QuickView/renderers/ProductionComponents';

import { TP } from 'constants/index';

import { getProgramLabel } from 'utils/common';
import { getComposerFromCreators } from 'utils/composer';
import { useDialogs } from 'utils/hooks/useDialogs';
import usePageContext from 'utils/hooks/usePageContext';
import { creditMatchWorks } from 'utils/productions';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './CreditsWorks.module.scss';

const formatCredits = credits => {
  const roleNames = uniqBy(credits);

  if (roleNames.length === 1) {
    return roleNames[0];
  }

  if (roleNames.length === 2) {
    return roleNames?.join(' & ');
  }

  return `${roleNames.slice(0, -1).join(', ')} & ${roleNames[roleNames.length - 1]}`;
};

const groupCreditsAndWorks = items =>
  items?.reduce((acc, item, index, arr) => {
    const existingItemIndex = acc.findIndex(a => a?.work?.id === item?.work?.id);

    if (existingItemIndex !== -1) {
      const isLastOccurrence = arr.slice(index + 1).findIndex(a => a?.work?.id === item?.work?.id) === -1;
      const existingCredit = acc[existingItemIndex].credit;
      const newCredit = item.credit;

      if (!existingCredit?.includes(newCredit)) {
        const separator = isLastOccurrence ? ' & ' : ', ';
        acc[existingItemIndex] = {
          ...acc[existingItemIndex],
          credit: existingCredit ? `${existingCredit}${separator}${newCredit}` : newCredit,
        };
      }
    } else {
      acc.push({ ...item });
    }

    return acc;
  }, []);

const transformCreditWorks = ({ production, items, isCastingProfile }) => {
  const programs = uniqBy(items, 'work.id');
  const customTitle = production?.name;
  const allCredits = formatCredits(items?.map(i => i.credit));
  const creditsAndWorks = groupCreditsAndWorks(items);
  const collapsedData = {
    credits: allCredits,
    ...(isCastingProfile
      ? { customTitle, programs }
      : {
          ...(customTitle ? { customTitle } : { programs }),
        }),
  };

  return {
    ...(programs.length > 0 && { expanded: creditsAndWorks }),
    ...(items?.length > 0 && { collapsed: collapsedData }),
  };
};

const CreditProgram = ({ program, trackingData, isExpanded, styles, hasMultiWorks, isJumpInSlug }) => {
  const { urlSlugs } = usePageContext();
  const isCastingProfile = urlSlugs?.[0]?.entityType === ENTITY_TYPES.PROFILE;
  const textColor = isExpanded || isCastingProfile || hasMultiWorks;

  if (!program?.work?.id) {
    return null;
  }

  return (
    <div
      className={classnames(classes.creditsWorks__program, { [classes.creditsWorks__programJumpIn]: !!isJumpInSlug })}
    >
      <Typography
        size={12}
        color={textColor || isJumpInSlug ? 'primary' : 'secondary'}
        className={classnames(classes.programWork, {
          [styles?.workName]: !!styles?.workName,
        })}
        weight={isJumpInSlug ? 'bold' : 'regular'}
      >
        {program?.work?.original_name}
        {', '}
      </Typography>
      {program.composer?.name && (
        <Typography
          size={12}
          color={textColor ? 'secondary' : 'tertiary'}
          italic
          className={classnames({
            [styles?.composer]: !!styles?.composer,
          })}
        >
          <EntityName
            entityType={ENTITY_TYPES.PROFILE}
            entity={{ id: program.composer?.id, name: program.composer.name }}
            isRaw
            trackingData={trackingData}
          />
        </Typography>
      )}
      {program?.work?.name?.toLocaleLowerCase() !== program?.work?.original_name?.toLocaleLowerCase() && (
        <Typography
          size={10}
          className={classnames(classes.creditsWorks__program_translatedName, {
            [styles?.translatedName]: !!styles?.translatedName,
          })}
          color={isExpanded ? 'secondary' : 'tertiary'}
        >
          ({program.work.name})
        </Typography>
      )}
      {program?.work?.workExcerptReference && (
        <Typography
          size={10}
          className={classes.creditsWorks__program_excerpt}
          color={isExpanded ? 'secondary' : 'tertiary'}
          italic
        >
          {program.work.workExcerptReference}
        </Typography>
      )}
    </div>
  );
};

const CreditsWorks = ({
  production,
  entityId,
  trackingData = {},
  entityType,
  isExpanded = false,
  styles,
  hasMultiWorks = false,
  isInLineView = false,
  isJumpInSlug,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isJumpInSelectProductionsModalOpen } = useDialogs();
  const { urlSlugs, navigate } = usePageContext();
  const isCastingProfile = urlSlugs?.[0]?.entityType === ENTITY_TYPES.PROFILE;
  const { creditsWorksComposers, matches } = useMemo(() => {
    const items = production?.matches || creditMatchWorks({ production, entityId, entityType });
    return {
      creditsWorksComposers: transformCreditWorks({ production, items, isCastingProfile }),
      matches: items,
    };
  }, []);

  const { isWorkRoleDebut, isCover } = useMemo(
    () => ({
      isWorkRoleDebut: matches?.some(match => match?.isRoleDebut),
      isCover: matches?.some(match => match?.isCover),
    }),
    [matches],
  );

  const { collapsed, expanded } = creditsWorksComposers || {};
  const { credits, customTitle, programs } = collapsed || {};
  const totalWorksInProduction = production?.productionWorks?.length || production?.workCount || 0;
  const programCount = totalWorksInProduction - programs?.length || 0;

  const showExpandedSectionData = useMemo(() => {
    const hasWorks = customTitle && programs?.some(program => program?.work?.id) && isExpanded;
    const hasSameCredit = uniqBy(programs, 'credit').length === 1;
    const shouldShowExpanded = expanded?.length > 0 && !production?.isProgramNotAnnounced && isExpanded;

    if (hasWorks) {
      return true;
    }

    if (hasSameCredit) {
      return false;
    }

    return shouldShowExpanded;
  }, []);

  const worksListing = useMemo(() => {
    if (isJumpInSelectProductionsModalOpen) {
      return production?.productionWorks?.map(work => ({
        ...work,
        workExcerptReference: work.workExcerptReference,
        composer: getComposerFromCreators(production?.productionWorks?.[0]?.work?.creators)?.profile,
      }));
    }

    return programs;
  }, [isJumpInSelectProductionsModalOpen, production?.productionWorks, programs]);

  const showProgramCount = useMemo(() => {
    if (programCount === 0) {
      return false;
    }

    const hasMoreWorks = totalWorksInProduction - (expanded?.length || 0) > 0;

    const shouldShowProgramCount = (customTitle && programCount > 0) || hasMoreWorks;

    return shouldShowProgramCount && !production?.isProgramNotAnnounced;
  }, []);

  if (isInLineView) {
    const stagedPart = production?.genre?.split('||')[1]?.trim();

    return (
      <div className={classes.creditsWorksInline}>
        <Typography size={12} weight="bold" className={classes.creditsWorksInline__credits}>
          {collapsed?.credits}
          {production?.isCover && (
            <Typography size={11} color="secondary" className={classes.coverRole}>
              ({t(`${TP}.FN_COVER`)})
            </Typography>
          )}
        </Typography>
        {(production?.workCount > 0 || stagedPart) && (
          <Typography size={11} color="secondary" className={classes.creditsWorksInline__count} truncate>
            {production?.workCount > 0
              ? `(${production.workCount} ${t(`${TP}.${production.workCount > 1 ? 'm_WORKS' : 'FN_WORK'}`)}) `
              : ''}
            {stagedPart && stagedPart}
            {production?.isRoleDebut && (
              <Typography size={11} color="secondary" className={classes.roleDebut}>
                (RWD)
              </Typography>
            )}
          </Typography>
        )}
      </div>
    );
  }

  return (
    <div className={classes.creditsWorks}>
      <div className={classes.creditsWorks__collapsed}>
        {credits?.length > 1 && (!isExpanded || !customTitle) && !isJumpInSlug && (
          <Typography
            size={12}
            weight="bold"
            className={classnames(classes.creditsWorks__collapsed_credit, { [styles?.credit]: !!styles?.credit })}
            color="primary"
          >
            {collapsed.credits}
            {isCover && (
              <Typography size={12} color="secondary" className={classes.coverRole}>
                ({t(`${TP}.FN_COVER`)})
              </Typography>
            )}
            {isWorkRoleDebut && (
              <Typography size={11} color="secondary" className={classes.roleDebut}>
                (RWD)
              </Typography>
            )}
          </Typography>
        )}
        {customTitle && (
          <Typography
            size={12}
            className={classnames(classes.creditsWorks__collapsed_customTitle, {
              [classes?.customTitleExpanded]: !!isExpanded,
              [styles?.customTitle]: !!styles?.customTitle,
            })}
            color="secondary"
          >
            {customTitle}
            {production?.workCount > 0 &&
              ` (${production.workCount} ${t(`${TP}.${production.workCount > 1 ? 'm_WORKS' : 'FN_WORK'}`)}) `}
          </Typography>
        )}
        {worksListing?.length > 0 && (
          <div
            className={classnames(classes.creditsWorks__collapsed_list, {
              [classes.disableText]: isJumpInSelectProductionsModalOpen,
            })}
          >
            {worksListing?.map((program, index) => (
              <CreditProgram
                key={index}
                program={program}
                trackingData={trackingData}
                isExpanded={isExpanded}
                styles={styles}
                hasMultiWorks={hasMultiWorks}
                isJumpInSlug={isJumpInSlug}
              />
            ))}
          </div>
        )}
        {showProgramCount && (
          <Typography
            size={11}
            color={isExpanded || hasMultiWorks || isCastingProfile ? 'secondary' : 'tertiary'}
            className={classnames(classes.creditsWorks__workCount, {
              [styles?.workCount]: !!styles?.workCount,
            })}
          >
            {t(`${TP}.FN_PROGRAM_COUNT`, { workCount: programCount })}
          </Typography>
        )}
        {production?.isProgramNotAnnounced && (
          <Typography
            size="10"
            color={isExpanded ? 'secondary' : 'tertiary'}
            italic
            className={classnames(classes.creditsWorks__collapsed_noProgram, {
              [styles?.noProgram]: !!styles?.noProgram,
            })}
          >
            {t(getProgramLabel(production, totalWorksInProduction))}
          </Typography>
        )}
      </div>
      <DurationAndLangInfo
        production={production}
        styles={{
          separatorListContainerBlock: classes.separatorListContainer,
          separatorItemBlock: classes.separatorItemBlock,
        }}
      />
      {isWorkRoleDebut && (
        <Typography size={12} className={classes.roleDebut}>
          {t(`${TP}.ROLE_WORK_DEBUT`)}
        </Typography>
      )}
      {showExpandedSectionData && (
        <div>
          {expanded?.map(({ credit, work, composer }, index) => {
            const previousCredit = index > 0 ? expanded[index - 1]?.credit : null;

            return (
              <div
                className={classnames(classes.creditsWorks__list, {
                  [classes.disableText]: isJumpInSelectProductionsModalOpen,
                })}
                key={work?.id}
              >
                {previousCredit !== credit && (
                  <Typography size={12} weight="bold" className={classes.credit}>
                    {credit}
                  </Typography>
                )}
                <div key={index} className={classes.work}>
                  {work?.original_name && (
                    <div className={classes.work__wrapper}>
                      <div className={classes.work__wrapper_nameComposer}>
                        <Typography size={12} className={classes.name}>
                          {work.original_name}
                        </Typography>
                        {composer?.name && (
                          <Typography size={12} className={classes.composer} color="secondary" italic>
                            {', '}
                            <EntityName
                              entityType={ENTITY_TYPES.PROFILE}
                              entity={{ id: composer?.id, name: composer.name }}
                              isRaw
                              trackingData={trackingData}
                            />
                          </Typography>
                        )}
                      </div>
                      <div>
                        {work?.name.toLocaleLowerCase() !== work?.original_name?.toLocaleLowerCase() && (
                          <Typography size={10} className={classes.translatedName} color="secondary">
                            ({work.name})
                          </Typography>
                        )}
                        {work?.workExcerptReference && (
                          <Typography size={10} className={classes.excerpt} color="secondary" italic>
                            {work.workExcerptReference}
                          </Typography>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {isExpanded && showProgramCount && programCount > 1 && (
        <LinkButton
          styles={{ root: classes.viewFullProgram }}
          variant="text"
          isLink
          trackingData={trackingData}
          rightIcon={<SpriteIcon icon="open_in_new" size={13} />}
          {...navigate.getLinkProps({ entity: production, entityType: ENTITY_TYPES.PRODUCTION })}
        >
          {t(`${TP}.FN_VIEW_FULL_PROGRAMS`)}
        </LinkButton>
      )}
    </div>
  );
};

export default CreditsWorks;
